import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export interface GoogleReview {
  id: string;
  company_id: string;
  location_id: string | null;
  place_id: string;
  review_id: string | null;
  reviewer_name: string;
  rating: number;
  review_text: string | null;
  review_date: string | null;
  google_review_url: string | null;
  is_actionable: boolean;
  is_responded: boolean;
  responded_at: string | null;
}

export function useGoogleReviews(showActionableOnly: boolean = false) {
  const { data: companyId } = useCurrentCompany();

  return useQuery({
    queryKey: ['google-reviews', companyId, showActionableOnly],
    queryFn: async () => {
      if (!companyId) return [];

      try {
        let query = (supabase as any)
          .from('google_reviews')
          .select('*')
          .eq('company_id', companyId)
          .order('review_date', { ascending: false })
          .limit(20);

        if (showActionableOnly) {
          query = query.eq('is_actionable', true);
        }

        const { data, error } = await query;

        if (error) {
          // If table doesn't exist (migration not applied), return empty array gracefully
          if (error.code === 'PGRST116' || error.message?.includes('does not exist')) {
            console.warn('Google reviews table not found. Migrations may not be applied.');
            return [];
          }
          console.error('Error fetching Google reviews:', error);
          throw error;
        }

        return (data || []) as unknown as GoogleReview[];
      } catch (error: any) {
        // Handle table not found gracefully
        if (error?.code === 'PGRST116' || error?.message?.includes('does not exist')) {
          console.warn('Google reviews table not found. Migrations may not be applied.');
          return [];
        }
        throw error;
      }
    },
    enabled: !!companyId,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useMarkReviewResponded() {
  const queryClient = useQueryClient();
  const { data: companyId } = useCurrentCompany();

  return useMutation({
    mutationFn: async (reviewId: string) => {
      try {
        const { error } = await (supabase as any)
          .from('google_reviews')
          .update({
            is_responded: true,
            responded_at: new Date().toISOString(),
          })
          .eq('id', reviewId);

        if (error) {
          // If table doesn't exist, fail silently (migrations not applied)
          if (error.code === 'PGRST116' || error.message?.includes('does not exist')) {
            console.warn('Google reviews table not found. Migrations may not be applied.');
            return;
          }
          throw error;
        }
      } catch (error: any) {
        // Handle table not found gracefully
        if (error?.code === 'PGRST116' || error?.message?.includes('does not exist')) {
          console.warn('Google reviews table not found. Migrations may not be applied.');
          return;
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-reviews', companyId] });
    },
  });
}
